/** @module csi/enums */
define([
	"dojo/_base/declare",
	"dojo/Deferred",
	"dojo/when",
	"underscore",
	"csi",
	"csi/locale",
	"csi/reflection",
	"csi/store/Request",
	"csi/xml",
	"csi/xml/Search"
], function(declare, Deferred, when, _, csi, locale, reflection, Request, xml, Search) {

	const _stores = {};
	const _oldEnums = {};

	function _getData(/*String*/id, /*Object[]?*/values, /*Boolean*/bEmpty, /*Function?*/filter) {
		// summary: create object store given EnumValue nodes
		var empty = xml.enumCreate(id);
		var data = bEmpty ? [{name: locale.getEnumName(empty) || csi.EMPTY_STRING, id: empty}] : [];
		var n = values ? values.length : 0;
		if (n) {
			var obj, v, t;

			for (var i = 0; i < n; ++i) {
				obj = values[i];

				// blank value is always added first, so do not add it again
				if (obj["$Name"]) {
					v = xml.enumCreate(id, obj["$Name"]);

					// do not add if localized name is empty
					if (t = locale.getEnumName(v)) {
						// apply filter (if any)
						if (!filter || filter(v, t, obj)) {
							data.push({name: obj.Active ? t : t + ' (' + locale.resource("ActionEnum_Invalid") + ')', id: v, index: i, active: obj.Active});
						}
					}
				}
			}
		}

		return data;
	};

	/**
	* Generate enum value from enum id and satisfy the _EnumValue api from reflection.js 
	* @param {string[]} values - array of enumid 
	* @return {Array} - array of enum value objects
	*/
	function _getObjectArray(values) {
		return _.map(values, function(val) {
			return {
				$Name: xml.enumGetText(val),
				Active: false,
				isValidDependsOnValue: function(value) {
					return true;
				}
			};
		});
	}

	/**
	* Set a flag on invalid enum values
	* @param {Store} store - data store of current enum type
	* @param {string} value - id of enum
	*/
	function _setInvalidFlag(store, value) {
		var item = store.get(value);
		if (item) {
			item.invalid = true;
		}

	}

	var _Store = declare(Request, {
		enumId: null,
		idProperty: "id",
		labelProperty: "name",
		indexProperty: "index",

		getEmptyValue: function() {
			// summary: return empty enum value
			return xml.enumCreate(this.enumId);
		},

		getDefaultValue: function(options) {
			// summary: interface to return default value for store using deferred object;
			//  if there are no values then return null;
			//	if there is only one non-empty value, then return that one;
			//	otherwise return first value
			var dfd = new Deferred();
			var id = this.idProperty;
			var empty = this.getEmptyValue();
			var store = this;

			options = options || {};
			options.start = 0;
			options.count = 3;

			when(this.query({}, options), function(results) {
				var x = null;
				var n = results.length;
				if (n == 1) {
					x = results[0][id];
				} else if (store.get(empty)) {
					x = (n == 2) ? results[1][id] : empty;
				}

				dfd.resolve(x);
			});

			return dfd;
		},

		isEmpty: function() {
			// summary: detect if the store has more values rather than empty string
			var storeOptions = this._store.data;
			var n = storeOptions.length;

			if (!n || ((n == 1) && (storeOptions[0].name == csi.EMPTY_STRING))) {
				return true;
			}

			return false;
		},

		_onResponse: function(/*Response*/res, /*Request*/req, /*Object*/options) {
			// summary: convert Names response into enum data
			var data = [];

			// hold the active enum values
			var activeValuesHash = {};

			// get the values if items were already selected previously
			var previousResults = this._store.query();
			var idProperty = this._store.idProperty;

			_.each(res.getNameAndUrls(), function(item) {
				activeValuesHash[item.Name] = true;
				data.push({
					$Name: item.Name,
					Active: true
				});
			});

			var inactiveResults = previousResults.filter(function(item) {
				return !activeValuesHash[xml.enumGetText(item[idProperty])];
			});

			// if some items are inactive, add them 
			if (!_.isEmpty(inactiveResults)) {
				_.each(inactiveResults, function(item) {
					var inactiveItem = {
						$Name: xml.enumGetText(item[idProperty]),
						Active: false
					};

					data.push(inactiveItem);
				});
			}

			this._setData(_getData(this.enumId, data, true));
		}
	});

	return {
		//TODO: Take care of empty enum values
		/*
		Rewrite this function to use following code:
			var enumId = xml.enumGetId(value);
			var enumStore = enums.getStore(enumId);
			var enumItem = enumStore && enumStore.get(value);
			return enumItem ? enumItem[enumStore.indexProperty] : this.getIndexForOldEnum(value);
		in order to make it possible _getData function (this module static code) should be modified
		line 
				var	data = bEmpty ? [{name: locale.getEnumName(empty) || csi.EMPTY_STRING, id: empty}] : [];
		should be changed to 
				var idx == 0;
				var	data = bEmpty ? [{name: locale.getEnumName(empty) || csi.EMPTY_STRING, id: empty, index:idx++}] : [];
				
		line 
							data.push({name: t, id: v, index: i});
		should be changed to 
							data.push({name: t, id: v, index: idx});
							
		also, on Admin web site, Configuration/Enumeerations/Edit Enum Values shoud be changed, so that EMPTY enum value would be always first and can'be re-arranged.
		Find and fix all placed that uses the fact that empty enum value has no index it. For instance, right after _getData function change, 
		works of inline editors for enum values will be disrupted and has to be fixed. More place might have attention 
		*/
		/**
		 * Return index of specified enum value
		 * @param {string} value - {enmId:enumValue}
		 * @return {Number}
		 */
		getEnumIndex: function(value) {
			var enumId = xml.enumGetId(value);
			var enumList = reflection.getEnumList(enumId);
			return enumList.getValueIndex(value);
		},

		getIndexForOldEnum: function(/*String*/name) {
			// summary: return index for enum that is no longer in original enum list that will be greater than any index of a current enum and can be used for sorting old enums alphabetically
			var n = _oldEnums[name];
			if (!n) {
				var names = _.keys(_oldEnums);
				if (names.length) {
					names.push(name);
					names.sort();

					var i = _.indexOf(names, name, true);
					var n1 = _oldEnums[names[i - 1]];
					var n2 = _oldEnums[names[i + 1]];
					if (n1 && n2) {
						n = (n1 + n2) / 2;
					} else if (n1) {
						n = n1 + 1000;
					} else {
						n = n2 - 1000;
					}
				} else {
					n = 5000000;
				}

				_oldEnums[name] = n;
			}

			return n;
		},

		/**
		 * Return store for given EnumList name using reflection cache - only cache data store if not filtering
		 * @param {String} id
		 * @param {String|String[]} value
		 * @param {Function} filter
		 * @param {Boolean} bRequired
		 * @return {Store}
		 */
		getStore: function(id, value, filter, bRequired) {
			var store = _stores[id];
			var bCache = true;
			var invalidValues = [];
			var enumList = reflection.getEnumList(id);
			var enumValues = enumList?.activeValues;
			var passedValues = value ? [].concat(value) : value;

			if (passedValues && passedValues.length) {
				invalidValues = _.filter(passedValues, function(val) {
					var txt = xml.enumGetText(val);
					return txt && csi.locate(enumValues, function(v) {return v.$Name == txt;}) < 0;
				});
			}

			// do not use cached store if filter or uncached value specified
			if (filter || bRequired || invalidValues.length) {
				store = null;
				bCache = false;
			}

			if (!store) {
				// if value exists and is not in enum list, then add it
				if (invalidValues.length) {
					// these invalid values will show up as values in drop-down for any depends on value
					var values = _getObjectArray(invalidValues);
					if (!enumValues) {
						enumValues = values;
					} else {
						enumValues = values.concat(enumValues);
					}
				}

				// if on Form, when it's not required, don't add the empty entry
				// otherwise, if current value is not null or this attribute is a required one, then don't all empty entry to the list
				var bAddEmpty = !bRequired || (value && !locale.getEnumName(value));;
				var data = _getData(id, enumValues, bAddEmpty, filter);

				// sort enums by name if ContainClassNames="true"
				if (enumList && enumList.containsClassNames) {
					data = csi.sort(data, "name");
					_.each(data, function(item, i) {item.index = i;});
				}

				store = new _Store({
					data: data,
					enumId: id
				});

				if (invalidValues.length) {
					_.each(invalidValues, function(val) {
						_setInvalidFlag(store, val);
					});
				}

				if (bCache) {
					_stores[id] = store;
				}
			}

			return store;
		},

		getServerStore: function(/*String*/enumId, /*String*/value) {
			// summary: return store for given EnumList using query to database - use Request
			const data = [];
			let search;

			if (enumId) {
				search = new Search({Operation: "NamesByXML"});

				search.getQuery()
					.Node(xml.PROP_TYPE, xml.OP_EQUALS, reflection.TYPE_ENUM_VALUE)
					.Node(xml.PROP_NAME, xml.OP_EQUALS, enumId, reflection.PATH_PARENT)
					.Attr("Active", xml.OP_EQUALS, true);

				search.getSortQuery().SortByIndex("Values");

				value = value || xml.enumCreate(enumId);

				data.push({
					id: value,
					name: locale.getEnumName(value)
				});
			}

			return new _Store({
				data,
				enumId,
				request: search?.getRequest()
			});
		},

		isEnumBaseType: function(/*String*/type) {
			// summary: checks if type passes is an enum or a container of enums.
			type = xml.attributeTypeGetBase(type) || type;
			return type == xml.ATTR_ENUM;
		},
	};
});